import React, { useState, useEffect, useCallback } from 'react';
import './GuidedProcedureSelector.css';

// Simplified interfaces - no AI matching needed
interface Procedure {
  id: number;
  name: string;
  category: string;
  description?: string;
}

interface SelectedNonsurgical {
  procedureId: number;
}

interface SelectedSurgical {
  procedureId: number;
  surgeryType: string;
}

interface GuidedProcedureSelectorProps {
  protocolId: number;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onCompiled: (nonsurgicalText: string, surgicalText: string) => void;
  onError: (error: string) => void;
}

const GuidedProcedureSelector: React.FC<GuidedProcedureSelectorProps> = ({
  protocolId,
  apiBaseUrl,
  getAuthHeaders,
  onCompiled,
  onError
}) => {
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isCompiling, setIsCompiling] = useState(false);

  // All procedures from database
  const [nonsurgicalProcedures, setNonsurgicalProcedures] = useState<Procedure[]>([]);
  const [surgicalProcedures, setSurgicalProcedures] = useState<Procedure[]>([]);

  // Selected procedures with details
  const [selectedNonsurgical, setSelectedNonsurgical] = useState<Map<number, SelectedNonsurgical>>(new Map());
  const [selectedSurgical, setSelectedSurgical] = useState<Map<number, SelectedSurgical>>(new Map());

  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Load all procedures from database
  useEffect(() => {
    const loadProcedures = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${apiBaseUrl}/api/procedures/all`, {
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('Failed to load procedures');
        }

        const data = await response.json();
        setNonsurgicalProcedures(data.nonsurgical || []);
        setSurgicalProcedures(data.surgical || []);

        // Auto-expand first category of each type if they have procedures
        const firstNonsurgicalCategory = data.nonsurgical?.[0]?.category;
        const firstSurgicalCategory = data.surgical?.[0]?.category;
        const initial = new Set<string>();
        if (firstNonsurgicalCategory) initial.add(`nonsurgical-${firstNonsurgicalCategory}`);
        if (firstSurgicalCategory) initial.add(`surgical-${firstSurgicalCategory}`);
        setExpandedCategories(initial);

      } catch (err) {
        onError(err instanceof Error ? err.message : 'Failed to load procedures');
      } finally {
        setIsLoading(false);
      }
    };

    loadProcedures();
  }, [apiBaseUrl, getAuthHeaders, onError]);

  // Group procedures by category
  const groupByCategory = (procedures: Procedure[]): Map<string, Procedure[]> => {
    const grouped = new Map<string, Procedure[]>();
    for (const proc of procedures) {
      const category = proc.category || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(proc);
    }
    return grouped;
  };

  const nonsurgicalByCategory = groupByCategory(nonsurgicalProcedures);
  const surgicalByCategory = groupByCategory(surgicalProcedures);

  // Toggle category expansion
  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  // Toggle nonsurgical selection
  const toggleNonsurgical = useCallback((proc: Procedure) => {
    setSelectedNonsurgical(prev => {
      const next = new Map(prev);
      if (next.has(proc.id)) {
        next.delete(proc.id);
      } else {
        next.set(proc.id, {
          procedureId: proc.id
        });
      }
      return next;
    });
  }, []);

  // Toggle surgical selection
  const toggleSurgical = useCallback((proc: Procedure) => {
    setSelectedSurgical(prev => {
      const next = new Map(prev);
      if (next.has(proc.id)) {
        next.delete(proc.id);
      } else {
        next.set(proc.id, {
          procedureId: proc.id,
          surgeryType: 'survival'
        });
      }
      return next;
    });
  }, []);

  // Update surgical details (surgery type only)
  const updateSurgicalDetails = useCallback((procId: number, surgeryType: string) => {
    setSelectedSurgical(prev => {
      const next = new Map(prev);
      const existing = next.get(procId);
      if (existing) {
        next.set(procId, { ...existing, surgeryType });
      }
      return next;
    });
  }, []);

  // Compile selected procedures
  const handleCompile = async () => {
    const totalSelected = selectedNonsurgical.size + selectedSurgical.size;
    if (totalSelected === 0) {
      onError('Please select at least one procedure');
      return;
    }

    try {
      setIsCompiling(true);

      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocolId}/compile-procedures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          nonsurgicalSelections: Array.from(selectedNonsurgical.values()),
          surgicalSelections: Array.from(selectedSurgical.values())
        })
      });

      if (!response.ok) {
        throw new Error('Failed to compile procedures');
      }

      const data = await response.json();
      onCompiled(data.nonsurgicalText || '', data.surgicalText || '');

    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to compile procedures');
    } finally {
      setIsCompiling(false);
    }
  };

  // Render a procedure card
  const renderProcedureCard = (
    proc: Procedure,
    isSelected: boolean,
    onToggle: () => void,
    details: SelectedNonsurgical | SelectedSurgical | undefined,
    type: 'nonsurgical' | 'surgical'
  ) => {
    return (
      <div
        key={proc.id}
        className={`procedure-card ${isSelected ? 'selected' : ''}`}
        onClick={onToggle}
      >
        <div className="card-header">
          <div className="checkbox">{isSelected ? '✓' : ''}</div>
          <div className="card-content">
            <span className="proc-name">{proc.name}</span>
            {proc.description && (
              <span className="proc-description">{proc.description}</span>
            )}
          </div>
        </div>

        {/* Inline surgery type selector for surgical procedures only */}
        {isSelected && details && type === 'surgical' && (
          <div className="card-details" onClick={e => e.stopPropagation()}>
            <div className="detail-field">
              <label>Surgery Type:</label>
              <div className="surgery-type-buttons">
                <button
                  type="button"
                  className={`type-btn ${(details as SelectedSurgical).surgeryType === 'survival' ? 'active' : ''}`}
                  onClick={() => updateSurgicalDetails(proc.id, 'survival')}
                >
                  Survival
                </button>
                <button
                  type="button"
                  className={`type-btn ${(details as SelectedSurgical).surgeryType === 'non_survival' ? 'active' : ''}`}
                  onClick={() => updateSurgicalDetails(proc.id, 'non_survival')}
                >
                  Non-Survival
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render category section
  const renderCategory = (
    categoryKey: string,
    categoryName: string,
    procedures: Procedure[],
    type: 'nonsurgical' | 'surgical'
  ) => {
    const isExpanded = expandedCategories.has(categoryKey);
    const selectedInCategory = procedures.filter(p =>
      type === 'nonsurgical' ? selectedNonsurgical.has(p.id) : selectedSurgical.has(p.id)
    ).length;

    return (
      <div key={categoryKey} className="category-section">
        <div
          className={`category-header ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleCategory(categoryKey)}
        >
          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          <span className="category-name">{categoryName}</span>
          <span className="category-count">
            {selectedInCategory > 0 && (
              <span className="selected-count">{selectedInCategory} selected · </span>
            )}
            {procedures.length} procedures
          </span>
        </div>

        {isExpanded && (
          <div className="category-procedures">
            {procedures.map(proc => {
              const isSelected = type === 'nonsurgical'
                ? selectedNonsurgical.has(proc.id)
                : selectedSurgical.has(proc.id);
              const details = type === 'nonsurgical'
                ? selectedNonsurgical.get(proc.id)
                : selectedSurgical.get(proc.id);
              const onToggle = () => type === 'nonsurgical'
                ? toggleNonsurgical(proc)
                : toggleSurgical(proc);

              return renderProcedureCard(proc, isSelected, onToggle, details, type);
            })}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="procedure-selector">
        <div className="procedure-loading">
          <div className="loading-spinner"></div>
          <p>Loading procedures...</p>
        </div>
      </div>
    );
  }

  const totalSelected = selectedNonsurgical.size + selectedSurgical.size;

  return (
    <div className="procedure-selector">
      <div className="selector-header">
        <h3>Select Procedures for Your Study</h3>
        <p>Click on procedures to select them, then fill in the details for each.</p>
      </div>

      {/* Non-Surgical Procedures */}
      <div className="procedure-section">
        <div className="section-header">
          <h4>Non-Surgical Procedures</h4>
          <span className="selection-count">
            {selectedNonsurgical.size} selected
          </span>
        </div>

        {nonsurgicalByCategory.size === 0 ? (
          <p className="no-procedures">No non-surgical procedures available</p>
        ) : (
          <div className="categories-list">
            {Array.from(nonsurgicalByCategory.entries()).map(([category, procs]) =>
              renderCategory(`nonsurgical-${category}`, category, procs, 'nonsurgical')
            )}
          </div>
        )}
      </div>

      {/* Surgical Procedures */}
      <div className="procedure-section">
        <div className="section-header">
          <h4>Surgical Procedures</h4>
          <span className="selection-count">
            {selectedSurgical.size} selected
          </span>
        </div>

        {surgicalByCategory.size === 0 ? (
          <p className="no-procedures">No surgical procedures available</p>
        ) : (
          <div className="categories-list">
            {Array.from(surgicalByCategory.entries()).map(([category, procs]) =>
              renderCategory(`surgical-${category}`, category, procs, 'surgical')
            )}
          </div>
        )}
      </div>

      {/* Compile Button */}
      <div className="selector-actions">
        <button
          className="compile-button"
          onClick={handleCompile}
          disabled={isCompiling || totalSelected === 0}
        >
          {isCompiling ? (
            <>
              <span className="button-spinner"></span>
              Compiling...
            </>
          ) : (
            <>
              Compile {totalSelected} Selected Procedure{totalSelected !== 1 ? 's' : ''} →
            </>
          )}
        </button>
        {totalSelected === 0 && (
          <p className="compile-hint">Select at least one procedure to continue</p>
        )}
      </div>
    </div>
  );
};

export default GuidedProcedureSelector;
